import { Phone, FileText, CheckCircle, UserPlus, Loader2, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRecentActivity, RecentActivity } from "@/hooks/useDashboard";
import { formatDistanceToNow } from "date-fns";

const typeConfig = {
  call: { icon: Phone, className: "bg-blue-500/15 text-blue-400 border-blue-500/20" },
  deal: { icon: CheckCircle, className: "bg-success/15 text-success border-success/20" },
  proposal: { icon: FileText, className: "bg-primary/15 text-primary border-primary/20" },
  lead: { icon: UserPlus, className: "bg-purple-500/15 text-purple-400 border-purple-500/20" },
};

export function ActivityFeed() {
  const { data: activities, isLoading } = useRecentActivity();

  if (isLoading) {
    return (
      <div className="glass card-gradient rounded-xl p-5">
        <div className="flex items-center gap-2 mb-5">
          <Activity className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Recent Activity</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="glass card-gradient rounded-xl p-5">
        <div className="flex items-center gap-2 mb-5">
          <Activity className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Recent Activity</h2>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted/50 flex items-center justify-center">
            <Activity className="h-6 w-6 text-muted-foreground/50" />
          </div>
          <p className="text-sm">No recent activity yet.</p>
          <p className="text-xs mt-1 text-muted-foreground/70">Add leads, deals, or make calls to see activity here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass card-gradient rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <div className="p-1.5 rounded-lg bg-primary/10">
          <Activity className="h-4 w-4 text-primary" />
        </div>
        <h2 className="text-lg font-semibold">Recent Activity</h2>
        <span className="ml-auto text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
          {activities.length} events
        </span>
      </div>
      
      <div className="space-y-1">
        {activities.map((activity, index) => {
          const config = typeConfig[activity.type];
          const Icon = config.icon;

          return (
            <div
              key={activity.id}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg transition-all duration-200 hover:bg-muted/30 group cursor-pointer",
                index === 0 && "bg-muted/20"
              )}
            >
              <div className={cn(
                "p-2 rounded-lg shrink-0 border transition-transform duration-200 group-hover:scale-105",
                config.className
              )}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                  {activity.title}
                </p>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {activity.description}
                </p>
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0 bg-muted/50 px-2 py-1 rounded-full">
                {formatDistanceToNow(new Date(activity.time), { addSuffix: true })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
